import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';

import PrivateSubmissionLayout from '@private-crm-app/components/PrivateSubmissionLayout/PrivateSubmissionLayout';
import Loading from '@components/Loading';
import { useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import { useUserContext } from '@private-crm-app/user-context';
import { PrivateSubmissionAppRoutes, CRM_ISSUE_ID, SUBMISSION_REQUEST_DATA } from '@private-crm-app/constants';
import { completeOnboarding } from '@private-crm-app/requests/user';
import { get } from '@private-crm-app/requests/api';
import { buildIssueUrl, getAgencyId, getCRMToken, getStoredSubmission } from '@private-crm-app/utils';
import { normalizeOnboardingModalFields } from '@utils/AppUtil';
import { splitFullName } from '@utils/NameUtil';
import { stateOptions } from '@utils/StateUtil';

import './style.scss';

const POLLING_INTERVAL = 500;
const POLLING_TIMEOUT = 10000;

interface SubmissionToastState {
  hasStoredSubmissionId: string | null;
  hasToastMarker: boolean;
  submissionId?: string;
}

export default function Onboarding() {
  const { auth0User } = useAuthManagerContext();
  const { user, refreshUser } = useUserContext();
  const history = useHistory();

  // Local flag state (citizen-accessible endpoint)
  const [flagsLoaded, setFlagsLoaded] = useState(false);
  const [accountsFeatureEnabled, setAccountsFeatureEnabled] = useState<boolean>(false);
  const [isOnboardingModalEnabled, setIsOnboardingModalEnabled] = useState<boolean>(true);
  const [onboardingModalFields, setOnboardingModalFields] = useState<string[]>(['name']);
  // normalizeOnboardingModalFields guarantees a clean, whitelisted set with `name` always present.

  // Contact has no firstName/lastName columns; names derive from the single `name` field.
  const initialName = splitFullName((user as any)?.name || '');

  // We'll use contactId instead of userId to match the backend changes
  const [contactId, setContactId] = useState<string | number | null>(null);
  const [firstName, setFirstName] = useState(initialName.firstName || auth0User?.given_name || '');
  const [lastName, setLastName] = useState(initialName.lastName || auth0User?.family_name || '');
  const [streetAddress, setStreetAddress] = useState((user as any)?.streetAddress || '');
  const [city, setCity] = useState((user as any)?.city || '');
  const [state, setState] = useState((user as any)?.state || '');
  const [zip, setZip] = useState((user as any)?.zip || '');
  const [phone, setPhone] = useState((user as any)?.cellPhone || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(true);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const streetAddressRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLSelectElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  // Debug: component mount
  useEffect(() => {
    console.log('[Onboarding] mounted');
  }, []);

  useEffect(() => {
    // Try to use the contact ID from different sources
    // First check if we have a user object with an ID
    if (user && user.id) {
      console.log('Setting contact ID from user.id:', user.id);
      setContactId(user.id);
    }
    // If not, check if we have an Auth0 user ID
    else if (auth0User && auth0User.sub) {
      console.log('Setting contact ID from auth0User.sub:', auth0User.sub);
      setContactId(auth0User.sub);
    }
  }, [user, auth0User]);

  // Prefill fields once the contact loads, without clobbering anything the user has already typed.
  useEffect(() => {
    if (!user) {
      return;
    }
    const parts = splitFullName((user as any)?.name || '');
    setFirstName((prev) => prev || parts.firstName || auth0User?.given_name || '');
    setLastName((prev) => prev || parts.lastName || auth0User?.family_name || '');
    setStreetAddress((prev) => prev || (user as any)?.streetAddress || '');
    setCity((prev) => prev || (user as any)?.city || '');
    setState((prev) => prev || (user as any)?.state || '');
    setZip((prev) => prev || (user as any)?.zip || '');
    setPhone((prev) => prev || (user as any)?.cellPhone || '');
  }, [user, auth0User]);

  // Load feature flags for the agency using a citizen-compatible endpoint
  useEffect(() => {
    const loadFlags = async () => {
      try {
        // Local preview fallback: allow ?agencyId= to drive flag loading when no stored
        // agency id exists (NODE_ENV-gated devPreview, see auth-manager-context).
        const previewParams = new URLSearchParams(window.location.search);
        const isDevPreview = previewParams.get('devPreview') === '1' && process.env.NODE_ENV !== 'production';
        const previewAgencyId = isDevPreview ? Number(previewParams.get('agencyId')) || 0 : 0;
        const agencyId = getAgencyId() || previewAgencyId;
        const pathAgencyId = agencyId ? String(agencyId) : '0';
        const headers = agencyId ? { 'x-agency-id': String(agencyId) } : undefined;
        console.log('[Onboarding][Flags] Start loading flags', { agencyId, pathAgencyId, headers });
        const url = `agency/${pathAgencyId}/flags`;
        console.log('[Onboarding][Flags] GET', url, { headers });
        const resp = await get<{
          isAccountsFeatureFlagEnabled: boolean;
          isOnboardingModalEnabled?: boolean;
          onboardingModalFields?: string[];
        }>({
          url,
          headers,
        });
        console.log('[Onboarding][Flags] Response', resp);
        setAccountsFeatureEnabled(!!resp?.isAccountsFeatureFlagEnabled);
        setIsOnboardingModalEnabled(resp?.isOnboardingModalEnabled ?? true);
        setOnboardingModalFields(normalizeOnboardingModalFields(resp?.onboardingModalFields));
      } catch (e) {
        console.error('[Onboarding][Flags] Error loading flags', e?.response?.data || e?.message || e);
        setAccountsFeatureEnabled(false);
      } finally {
        console.log('[Onboarding][Flags] Flags loaded');
        setFlagsLoaded(true);
      }
    };
    loadFlags();
  }, []);

  // Set initial focus to first name when modal opens
  useEffect(() => {
    if (showModal) {
      // Defer to ensure element exists
      setTimeout(() => firstNameRef.current?.focus(), 0);
    }
  }, [showModal]);

  // If accounts feature is disabled, redirect to original path or listing page
  useEffect(() => {
    console.log('[Onboarding] Redirect guard check', { flagsLoaded, accountsFeatureEnabled });
    if (!flagsLoaded) {
      return;
    }

    if (!accountsFeatureEnabled || !isOnboardingModalEnabled) {
      console.log('Onboarding modal disabled - skipping onboarding');

      // Try to get the original path that led to onboarding
      const originalPath = localStorage.getItem('auth_original_path');
      const currentSearch = window.location.search;

      if (originalPath && originalPath !== '/private-submission/onboarding') {
        // Preserve the original URL with query params
        const redirectUrl = originalPath + currentSearch;
        console.log('Redirecting back to original path:', redirectUrl);
        history.push(redirectUrl);
      } else {
        // Fallback to listing if no original path
        history.push(PrivateSubmissionAppRoutes.LISTING);
      }

      // Clean up stored path
      localStorage.removeItem('auth_original_path');
    }
  }, [flagsLoaded, accountsFeatureEnabled, isOnboardingModalEnabled, history]);

  const shouldShowNameFields = onboardingModalFields.includes('name');
  const shouldShowAddressField = onboardingModalFields.includes('address');
  const shouldShowPhoneField = onboardingModalFields.includes('phone');

  const getIssueRedirectUrl = (): string => {
    const submission = getStoredSubmission();
    const issueId = submission?.id ?? localStorage.getItem(CRM_ISSUE_ID);
    return issueId
      ? buildIssueUrl(issueId, getCRMToken())
      : PrivateSubmissionAppRoutes.ISSUE_BASE_PATH;
  };

  const checkSubmissionToastState = (): SubmissionToastState => {
    const hasStoredSubmissionId = localStorage.getItem(CRM_ISSUE_ID);
    const hasToastMarker = hasStoredSubmissionId 
      ? sessionStorage.getItem(`submission_created_toast_${hasStoredSubmissionId}`) === '1'
      : false;
    
    return {
      hasStoredSubmissionId,
      hasToastMarker,
      submissionId: hasStoredSubmissionId || undefined,
    };
  };

  const redirectToSubmissionDetails = () => {
    setShowModal(false);
    history.push(getIssueRedirectUrl());
  };

  const handleNormalClose = () => {
    setShowModal(false);

    const originalPath = localStorage.getItem('auth_original_path');
    const currentSearch = window.location.search;

    if (originalPath && originalPath !== '/private-submission/onboarding') {
      const redirectUrl = originalPath + currentSearch;
      localStorage.removeItem('auth_original_path');
      history.push(redirectUrl);
    } else {
      history.push(PrivateSubmissionAppRoutes.LISTING);
    }
  };

  const waitForSubmissionCompletion = (): Promise<SubmissionToastState> => {
    return new Promise((resolve) => {
      let pollCount = 0;
      const maxPolls = POLLING_TIMEOUT / POLLING_INTERVAL;

      const pollInterval = setInterval(() => {
        pollCount++;
        const stillInProgress = localStorage.getItem(SUBMISSION_REQUEST_DATA);
        
        if (!stillInProgress || pollCount >= maxPolls) {
          clearInterval(pollInterval);
          const toastState = checkSubmissionToastState();
          resolve(toastState);
        }
      }, POLLING_INTERVAL);
    });
  };

  const handleClose = async () => {
    const hasSubmissionInProgress = localStorage.getItem(SUBMISSION_REQUEST_DATA);
    
    if (hasSubmissionInProgress) {
      try {
        const toastState = await waitForSubmissionCompletion();
        
        if (toastState.hasToastMarker) {
          redirectToSubmissionDetails();
        } else {
          handleNormalClose();
        }
      } catch (error) {
        handleNormalClose();
      }
      
      return;
    }

    // Check if there's a pending submission that needs to show success toast
    const toastState = checkSubmissionToastState();
    
    if (toastState.hasToastMarker) {
      redirectToSubmissionDetails();
      return;
    }

    // Normal close flow
    handleNormalClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (shouldShowNameFields && (!firstName || !lastName)) {
      setError('Please enter both first and last name');
      if (!firstName) {
        firstNameRef.current?.focus();
      } else if (!lastName) {
        lastNameRef.current?.focus();
      }
      return;
    }

    if (shouldShowAddressField) {
      if (!streetAddress.trim()) {
        setError('Please enter your street address');
        streetAddressRef.current?.focus();
        return;
      }
      if (!city.trim()) {
        setError('Please enter your city');
        cityRef.current?.focus();
        return;
      }
      if (!state.trim()) {
        setError('Please select your state');
        stateRef.current?.focus();
        return;
      }
      if (!zip.trim()) {
        setError('Please enter your ZIP code');
        zipRef.current?.focus();
        return;
      }
    }

    if (shouldShowPhoneField && !phone.trim()) {
      setError('Please enter your phone number');
      phoneRef.current?.focus();
      return;
    }

    // Log all available information to help debug
    console.log('User context:', user);
    console.log('Auth0 user:', auth0User);
    console.log('Current contactId state:', contactId);

    // Determine which ID to use - from our state or directly from auth0User.sub if available
    const idToUse = contactId || (auth0User && auth0User.sub);

    if (!idToUse) {
      console.error('No contact ID available from any source');
      setError('Contact ID is missing. Please try refreshing the page or contact support.');
      return;
    }

    setIsLoading(true);
    setShowModal(false); // Hide the modal immediately when submission starts
    setError('');

    try {
      console.log('Submitting onboarding with contact ID:', idToUse);
      console.log('Complete payload:', {
        contactId: idToUse,
        firstName,
        lastName,
        auth0UserId: auth0User?.sub,
      });

      // Pass the auth0UserId to the completeOnboarding function
      await completeOnboarding(idToUse, {
        firstName,
        lastName,
        auth0UserId: auth0User?.sub,
        ...(shouldShowAddressField
          ? {
              streetAddress: streetAddress.trim(),
              city: city.trim(),
              state: state.trim(),
              zip: zip.trim(),
            }
          : {}),
        ...(shouldShowPhoneField ? { phone: phone.trim() } : {}),
      });

      // Refresh the user data
      await refreshUser();

      // Check if there's a pending submission that needs to show success toast
      const toastState = checkSubmissionToastState();
      
      if (toastState.hasToastMarker) {
        history.push(getIssueRedirectUrl());
      } else {
        handleNormalClose();
      }
    } catch (err) {
      console.error('Error completing onboarding:', err);
      console.error('Error response:', err.response?.data);

      // Show the modal again if there's an error
      setShowModal(true);

      // Handle 404 (contact not found) errors specially
      if (err.response?.status === 404) {
        setError(
          'Your contact record is not properly set up in the system. Please contact support to complete your registration.'
        );
      } else {
        setError(err.response?.data?.message || 'An error occurred during onboarding. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PrivateSubmissionLayout>
      {showModal && (
        <Modal
          show={true}
          backdrop="static"
          keyboard={false}
          centered
          className="onboarding-modal"
          size="lg"
          onHide={handleClose}
        >
          <Modal.Body className="onboarding-modal-body">
            <Card className="onboarding-card">
              <Card.Header>
                <h2>Please enter your details below.</h2>
              </Card.Header>
              <Card.Body>
                {error && (
                  <Alert variant="danger" role="alert" aria-live="polite">
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  {shouldShowNameFields && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="Enter your first name"
                          required
                          autoComplete="given-name"
                          inputMode="text"
                          autoCapitalize="words"
                          ref={firstNameRef}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Enter your last name"
                          required
                          autoComplete="family-name"
                          inputMode="text"
                          autoCapitalize="words"
                          ref={lastNameRef}
                        />
                      </Form.Group>
                    </>
                  )}

                  {shouldShowPhoneField && (
                    <Form.Group className="mb-3">
                      <Form.Label>Cell Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="(555) 000-0000"
                        required
                        autoComplete="tel"
                        inputMode="tel"
                        ref={phoneRef}
                      />
                    </Form.Group>
                  )}

                  {shouldShowAddressField && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Street Address</Form.Label>
                        <Form.Control
                          type="text"
                          value={streetAddress}
                          onChange={(e) => setStreetAddress(e.target.value)}
                          placeholder="123 Main St"
                          required
                          autoComplete="address-line1"
                          inputMode="text"
                          ref={streetAddressRef}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="San Francisco"
                          required
                          autoComplete="address-level2"
                          inputMode="text"
                          ref={cityRef}
                        />
                      </Form.Group>

                      <div className="onboarding-address-row mb-4">
                        <Form.Group className="onboarding-state-field">
                          <Form.Label>State</Form.Label>
                          <Form.Select
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            required
                            autoComplete="address-level1"
                            ref={stateRef}
                          >
                            <option value="">Select state</option>
                            {stateOptions().map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="onboarding-zip-field">
                          <Form.Label>ZIP Code</Form.Label>
                          <Form.Control
                            type="text"
                            value={zip}
                            onChange={(e) => setZip(e.target.value)}
                            placeholder="94102"
                            required
                            autoComplete="postal-code"
                            inputMode="numeric"
                            ref={zipRef}
                          />
                        </Form.Group>
                      </div>
                    </>
                  )}

                  <div className="onboarding-actions d-grid" style={{ gap: '0.75rem' }}>
                    <Button variant="primary" type="submit" disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Continue'}
                    </Button>
                    <Button variant="outline-secondary" type="button" onClick={handleClose} disabled={isLoading}>
                      Close
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Modal.Body>
        </Modal>
      )}
      <Loading show={isLoading} />
    </PrivateSubmissionLayout>
  );
}
